import { zodResolver } from '@hookform/resolvers/zod';
import { useState, type JSX } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useSearchParams } from 'react-router';
import { authApi } from '../api/client.js';
import { isApiError, userErrorMessage } from '../api/errors.js';
import { AuthLayout } from '../components/AuthLayout.js';
import { Field } from '../components/Field.js';
import { FormMessage } from '../components/FormMessage.js';
import { SubmitButton } from '../components/SubmitButton.js';
import type { ResetValues } from '../auth/schemas.js';
import { useAuthSchemas } from '../auth/useAuthSchemas.js';
import { useI18n } from '../i18n/context.js';

export function ResetPasswordPage(): JSX.Element {
  const { reset: resetSchema } = useAuthSchemas();
  const { t } = useI18n();
  const [params] = useSearchParams();
  const token = params.get('token');
  const [done, setDone] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ResetValues>({ resolver: zodResolver(resetSchema) });

  const onSubmit = handleSubmit(async (values) => {
    if (!token) return;
    setFormError(null);
    try {
      await authApi.resetPassword(token, values.password);
      setDone(true);
    } catch (error: unknown) {
      setFormError(
        isApiError(error) && error.code === 'invalid_token'
          ? t.auth.reset.badLink
          : userErrorMessage(error, t),
      );
    }
  });

  // Без токена форму показывать нечестно: отправлять всё равно нечего.
  if (!token) {
    return (
      <AuthLayout
        title={t.auth.reset.title}
        footer={<Link to="/forgot-password">{t.auth.reset.requestNew}</Link>}
      >
        <FormMessage kind="error">{t.auth.reset.badLink}</FormMessage>
      </AuthLayout>
    );
  }

  if (done) {
    return (
      <AuthLayout title={t.auth.reset.title} footer={<Link to="/login">{t.auth.signIn}</Link>}>
        <FormMessage kind="success">{t.auth.reset.done}</FormMessage>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title={t.auth.reset.title}
      subtitle={t.auth.reset.subtitle}
      footer={<Link to="/forgot-password">{t.auth.reset.requestNew}</Link>}
    >
      <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
        {formError ? <FormMessage kind="error">{formError}</FormMessage> : null}

        <Field
          label={t.common.password}
          type="password"
          autoComplete="new-password"
          hint={t.auth.register.passwordHint}
          error={errors.password?.message}
          {...register('password')}
        />

        <SubmitButton label={t.auth.reset.submit} pending={isSubmitting} />
      </form>
    </AuthLayout>
  );
}
